import { useState } from 'react';

/**
 * The editing state the rule panels share (Validation Rules, Renewal Rules):
 * a dialog open on a new or saved rule, and the row actions a viewer may use.
 */

/** The rule dialog: closed, or open on a new rule (`ruleId` null) or a saved one. */
type RuleEditing<Form> =
  { mode: 'closed' } | { mode: 'open'; ruleId: string | null; form: Form };

const CLOSED = { mode: 'closed' } as const;

/** How a panel saves the open rule; `onSuccess` closes the dialog. */
export interface RuleSaveMutations<Draft> {
  update: (ruleId: string, draft: Draft, onSuccess: () => void) => void;
  create: (draft: Draft, onSuccess: () => void) => void;
}

type SetRuleEditing<Form> = (
  next: (current: RuleEditing<Form>) => RuleEditing<Form>,
) => void;

/**
 * Saves the open rule: updates a saved one, creates a new one. Closing
 * belongs to this Save alone: a switch toggled on another rule, or a dialog
 * opened while this save was in flight, must keep its edits.
 */
export function saveEditedRule<Form, Draft>(
  editing: RuleEditing<Form>,
  draft: Draft,
  mutations: RuleSaveMutations<Draft>,
  setEditing: SetRuleEditing<Form>,
): void {
  if (editing.mode !== 'open') return;
  const saving = editing;
  const closeIfStillOpen = () =>
    setEditing((current) => (current === saving ? CLOSED : current));
  if (saving.ruleId) mutations.update(saving.ruleId, draft, closeIfStillOpen);
  else mutations.create(draft, closeIfStillOpen);
}

export interface RuleRowPermissions {
  canUpdate: boolean;
  canDelete: boolean;
}

export interface RuleRowHandlers<Rule> {
  edit: (rule: Rule) => void;
  toggle: (rule: Rule, enabled: boolean) => void;
  remove: (rule: Rule) => void;
}

/** A row's actions; null hides one the viewer may not use. */
export interface RuleRowActions {
  onEdit: (() => void) | null;
  onToggle: ((enabled: boolean) => void) | null;
  onDelete: (() => void) | null;
}

export function ruleRowActions<Rule>(
  rule: Rule,
  permissions: RuleRowPermissions,
  handlers: RuleRowHandlers<Rule>,
): RuleRowActions {
  return {
    onEdit: permissions.canUpdate ? () => handlers.edit(rule) : null,
    onToggle: permissions.canUpdate
      ? (enabled) => handlers.toggle(rule, enabled)
      : null,
    onDelete: permissions.canDelete ? () => handlers.remove(rule) : null,
  };
}

/** What a panel does with its rules; the row actions are built from it. */
export interface RuleEditingOptions<Rule, Form> extends RuleRowPermissions {
  formFromRule: (rule: Rule) => Form;
  toggle: RuleRowHandlers<Rule>['toggle'];
  remove: RuleRowHandlers<Rule>['remove'];
}

/**
 * The rule dialog's state, opened on a new or saved rule and saved through
 * `save`, and each row's actions (`rowActions`), Edit opening the dialog.
 */
export function useRuleEditing<Rule extends { id: string }, Form>({
  formFromRule,
  toggle,
  remove,
  ...permissions
}: RuleEditingOptions<Rule, Form>) {
  const [editing, setEditing] = useState<RuleEditing<Form>>(CLOSED);
  const open = (ruleId: string | null, form: Form) =>
    setEditing({ mode: 'open', ruleId, form });
  return {
    editing,
    open,
    close: () => setEditing(CLOSED),
    save: <Draft>(draft: Draft, mutations: RuleSaveMutations<Draft>) =>
      saveEditedRule(editing, draft, mutations, setEditing),
    rowActions: (rule: Rule) =>
      ruleRowActions(rule, permissions, {
        edit: (edited) => open(edited.id, formFromRule(edited)),
        toggle,
        remove,
      }),
  };
}
